<template>
  <div class="shooter-list">
    <!-- 子页面操作栏上方新增父级信息区域 -->
    <el-card class="parent-info-card" shadow="hover">
      <!-- 面包屑导航 -->
      <el-breadcrumb separator-class="el-icon-arrow-right" style="margin-bottom: 10px;">
        <el-breadcrumb-item :to="{ name: 'ShootingActivityList' }">射击场次列表</el-breadcrumb-item>
        <el-breadcrumb-item>{{ activityName }}</el-breadcrumb-item>
        <el-breadcrumb-item>场次内人员列表</el-breadcrumb-item>
      </el-breadcrumb>
      <div class="shooter-type-info">
        <div class="info-row">
          <span class="info-label">射击场次：</span>
          <span class="info-value">{{ activityName }}</span>
        </div>
      </div>
    </el-card>
    <el-row :gutter="20" class="operate-bar">
      <el-col :span="8" align="left">
        <!-- 新增人员按钮 -->
        <el-button icon="el-icon-plus" type="primary" @click="openAddDialog"
          v-hasPermission="'activity:edit'">新增人员</el-button>
        <!-- 批量删除按钮（选中人员后可用） -->
        <el-button icon="el-icon-delete" type="danger" v-hasPermission="'activity:edit'" @click="handleBatchDelete"
          :disabled="!this.hasPermission('activity:edit') || selectedIds.length === 0">批量删除</el-button>
        <el-button icon="el-icon-upload2" @click="openImportDialog" v-hasPermission="'activity:edit'">批量导入</el-button>
        <!-- 返回父页面按钮 -->
        <el-button icon="el-icon-back" @click="$router.back()">
          返回射击场次列表</el-button>
      </el-col>
      <!-- 搜索区域 -->
      <el-col :span="16" align="right">
        <el-form :inline="true" :model="searchForm" class="search-form">
          <el-form-item label="人员本地编号">
            <el-input v-model="searchForm.shooterNo" placeholder="请输入人员本地编号" maxlength="6"
            @input="searchForm.shooterNo = searchForm.shooterNo.replace(/[^\d]/g, '').replace(/^0+/, '')"></el-input>
          </el-form-item>
          <el-form-item label="人员姓名">
            <el-input v-model="searchForm.shooterName" placeholder="请输入人员姓名" maxlength="20"></el-input>
          </el-form-item>
          <el-button icon="el-icon-search" type="primary" @click="loadShooters">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </el-form>
      </el-col>
    </el-row>

    <!-- 数据表格 -->
    <el-table :data="shooters" border stripe style="width: 100%" @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="55" align="center"></el-table-column>
      <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
      <el-table-column prop="shooterNo" label="人员本地编号" align="center"></el-table-column>
      <el-table-column prop="shooterName" label="人员姓名" align="center"></el-table-column>
      <el-table-column label="操作" align="center">
        <template slot-scope="scope">
          <el-tooltip content="编辑" placement="top">
            <el-button icon="el-icon-edit" type="primary" circle v-hasPermission="'activity:edit'"
              @click="openEditDialog(scope.row)"></el-button>
          </el-tooltip>
          <el-tooltip content="删除" placement="top">
            <el-button icon="el-icon-delete" type="danger" circle v-hasPermission="'activity:edit'"
              @click="handleDelete(scope.row.id)"></el-button>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="currentPage"
      :page-sizes="[10, 20, 50]" :page-size="pageSize" layout="total, sizes, prev, pager, next, jumper"
      :total="total"></el-pagination>

    <!-- 新增/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px">
      <el-form :model="form" :rules="rules" ref="form" label-width="150px">
        <el-form-item label="人员本地编号" prop="shooterNo">
          <el-input v-model="form.shooterNo" maxlength="6"
          @input="form.shooterNo = form.shooterNo.replace(/[^\d]/g, '').replace(/^0+/, '')"></el-input>
        </el-form-item>
        <el-form-item label="人员姓名" prop="shooterName">
          <el-input v-model="form.shooterName" maxlength="20"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false; $refs.form.resetFields()">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>

    <!-- 导入对话框 -->
    <el-dialog :visible.sync="importDialogVisible" title="批量导入" width="600px">
      <el-form :model="importForm">
        <el-form-item prop="file">
          <el-upload class="upload-demo" action="#" :auto-upload="false" :on-change="handleFileChange" :limit="1"
            accept=".xlsx, .xls" :file-list="fileList">
            <el-button type="primary">点击上传</el-button>
            <div slot="tip" class="template-tip">仅支持.xlsx/.xls格式，不超过5MB</div>
          </el-upload>
        </el-form-item>
        <el-form-item>
          <el-button type="info" @click="downloadTemplate">下载导入模板</el-button>
          <div class="template-tip">模板包含必填项和格式限制，请严格按照模板填写</div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="cancelImport">取消</el-button>
        <el-button type="primary" @click="submitImport">确认导入</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import activityApi from '../../api/activity'
import shooterApi from '../../api/shooter'
import { hasPermission } from '../../utils/hasPermission'

export default {
  name: 'ShooterList',
  data() {
    // 同场次内人员本地编号唯一校验
    const checkShooterNo = async (rule, value, callback) => {
      if (!value) {
        // 空值不校验（由required规则处理）
        return callback()
      }

      try {
        const params = {
          shooterNo: value,
          activityId: this.activityId,
          excludeId: this.isEdit ? this.recordId : null
        }
        // 调用后端查重接口
        const response = await shooterApi.checkShooterNo(params)
        if (response.success && response.exists) {
          // 人员本地编号已在当前场次中存在，返回错误提示
          callback(new Error('该人员本地编号已在当前场次中被使用，请更换'))
        } else {
          // 人员本地编号可用，验证通过
          callback()
        }
      } catch (error) {
        callback(new Error('检查失败，请稍后重试'))
      }
    }

    return {
      // 表格数据
      shooters: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      activityId: null, // 场次ID

      activityName: '', // 场次名称

      // 搜索表单
      searchForm: {
        shooterName: '',
        shooterNo: ''
      },

      // 导入相关
      importDialogVisible: false,
      importForm: {
        file: null
      },
      fileList: [], // el-upload 组件的文件列表

      // 选中的行
      selectedIds: [],

      // 对话框
      dialogVisible: false,
      dialogTitle: '新增人员',
      isEdit: false,
      recordId: null,
      form: {
        id: null,
        activityId: this.activityId,
        shooterName: '',
        shooterNo: ''
      },
      rules: {
        shooterName: [
          { required: true, message: '请输入人员姓名', trigger: 'blur' },
          { min: 1, max: 20, message: '人员姓名长度在 1 到 20 个字符', trigger: 'blur' }
        ],
        shooterNo: [
          { required: true, message: '请输入人员本地编号', trigger: 'blur' },
          // 验证正整数（不能以0开头，仅允许数字）
          { pattern: /^[1-9]\d*$/, message: '只能输入正整数，且不能以0开头', trigger: 'blur' },
          // 验证数值范围 1-999999
          { min: 1, max: 999999, message: '人员本地编号范围为 1 到 999999', trigger: 'blur' },
          { validator: checkShooterNo, trigger: 'blur' }
        ]
      }
    }
  },

  mounted() {
    this.activityId = this.$route.query.activityId;
    this.loadActivityInfo(this.activityId); // 加载场次信息
    this.loadShooters()
  },

  methods: {
    // 加载人员列表
    loadShooters() {
      // 组装请求参数
      const params = {
        shooterName: this.searchForm.shooterName, // 搜索-人员姓名
        shooterNo: this.searchForm.shooterNo,       // 搜索-人员本地编号
        activityId: this.activityId,
        page: this.currentPage,           // 分页-当前页
        size: this.pageSize               // 分页-每页条数
      };
      shooterApi.findAll(params).then(response => {
        if (response.success) {
          this.shooters = response.data.data
          this.total = response.data.total
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('加载人员失败：' + error.message)
      })
    },
    // 加载场次信息
    loadActivityInfo(activityId) {
      activityApi.findById(activityId).then(res => {
        if (res.success) {
          this.activityName = res.data.activityName;
        }
      });
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        shooterName: '',
        shooterNo: ''
      }
      this.loadShooters()
    },

    // 每页条数变化时
    handleSizeChange(val) {
      this.pageSize = val;
      this.loadShooters(); // 重新加载
    },

    // 当前页变化时
    handleCurrentChange(val) {
      this.currentPage = val;
      this.loadShooters(); // 重新加载
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedIds = selection.map(item => item.id)
    },

    // 打开新增对话框
    openAddDialog() {
      if (!hasPermission('activity:edit')) {
        this.$message.error('无新增人员权限');
        return;
      }
      this.dialogTitle = '新增人员'
      this.isEdit = false
      this.recordId = null
      this.form = {
        id: null,
        activityId: this.activityId,
        shooterName: '',
        shooterNo: ''
      }
      this.dialogVisible = true
    },

    // 打开编辑对话框
    openEditDialog(row) {
      if (!hasPermission('activity:edit')) {
        this.$message.error('无编辑人员权限');
        return;
      }
      this.dialogTitle = '编辑人员'
      this.isEdit = true
      this.recordId = row.id
      this.form = { ...row }
      this.dialogVisible = true
    },

    // 提交表单
    submitForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          if (this.isEdit) {
            // 更新人员
            shooterApi.update(this.form.id, this.form).then(response => {
              if (response.success) {
                this.$message.success(response.message || '更新成功')
                this.dialogVisible = false
                this.loadShooters()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('更新失败：' + error.message)
            })
          } else {
            // 新增人员
            shooterApi.add(this.form).then(response => {
              if (response.success) {
                this.$message.success(response.message || '新增成功')
                this.dialogVisible = false
                this.loadShooters()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('新增失败：' + error.message)
            })
          }
        }
      })
    },

    // 删除人员
    handleDelete(id) {
      if (!hasPermission('activity:edit')) {
        this.$message.error('无删除人员权限');
        return;
      }
      this.$confirm('确定要删除该人员吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        shooterApi.delete(id).then(response => {
          if (response.success) {
            this.$message.success(response.message || '删除成功')
            this.loadShooters()
          } else {
            this.$message.error(response.message)
          }
        }).catch(error => {
          this.$message.error('删除失败：' + error.message)
        })
      }).catch(() => {
        // 取消操作（此处可留空，仅用于捕获异常）
      });
    },

    // 批量删除人员
    handleBatchDelete() {
      if (!hasPermission('activity:edit')) {
        this.$message.error('无批量删除人员权限');
        return;
      }
      if (this.selectedIds.length === 0) {
        this.$message.warning('请选择要删除的人员');
        return;
      }
      this.$confirm('确定要批量删除选中的人员吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        shooterApi.deleteBatch(this.selectedIds).then(response => {
          if (response.success) {
            this.$message.success(response.message || '批量删除成功')
            this.loadShooters()
            this.selectedIds = []
          } else {
            this.$message.error(response.message)
          }
        }).catch(error => {
          this.$message.error('批量删除失败：' + error.message)
        })
      }).catch(() => {
        // 取消操作（此处可留空，仅用于捕获异常）
      });
    },

    hasPermission(permCode) {
      return hasPermission(permCode);
    },

    // 导入相关方法
    openImportDialog() {
      if (!hasPermission('activity:edit')) {
        this.$message.error('无导入人员权限');
        return;
      }
      this.importDialogVisible = true;
      this.$nextTick(() => {
        this.importForm.file = null;
      });
    },

    handleFileChange(file, fileList) {
      this.fileList = fileList;
      this.importForm.file = file.raw;
    },

    cancelImport() {
      this.fileList = [];
      this.importForm.file = null;// 清除已选择的文件
      this.importDialogVisible = false; // 关闭对话框
    },

    // 下载模板
    downloadTemplate() {
      if (!hasPermission('activity:edit')) {
        this.$message.error('无下载导入模板权限');
        return;
      }

      shooterApi.downloadTemplate().then(response => {
        console.log("响应状态码：", response.status);
        console.log("响应数据类型：", typeof response.data.constructor.name);
        // 处理返回的Excel二进制流，实现文件下载
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = '场次内人员导入模板.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }).catch(err => {
        console.error("请求错误详情：", err);
        this.$message.error('模板下载失败：' + err.message);
      });
    },

    // 确认导入
    submitImport() {
      // 检查是否选择了文件
      if (!this.importForm.file) {
        this.$alert('请选择Excel文件', '提示', {
          confirmButtonText: '确定',
          type: 'warning' // 警告类型（可选，控制图标）
        });
        return;
      }

      // 构建FormData
      const formData = new FormData();
      formData.append('file', this.importForm.file); // 添加文件
      formData.append('activityId', this.activityId); // 添加场次ID

      // 调用API
      shooterApi.importShooters(formData).then(response => {
        const result = response;
        if (result.success) {
          this.$message.success(`导入成功：成功${result.successCount}条，失败${result.failCount}条`);
          // 显示失败详情（如有）
          if (result.failMessages && result.failMessages.length > 0) {
            this.$alert(result.failMessages.join('<br>'), '导入失败详情', {
              dangerouslyUseHTMLString: true,
              confirmButtonText: '确定'
            });
          }
          this.importDialogVisible = false;
          this.loadShooters(); // 刷新人员列表
          this.cancelImport();
        } else {
          this.$message.error(result.failMessages || '导入失败');
        }
      }).catch(err => {
        this.$message.error(`导入失败：${err.message || '服务器错误'}`);
      });
    },
  }
}
</script>

<style scoped>
.parent-info-card {
  margin-bottom: 20px;
  border-left: 3px solid #409EFF;
  /* 保持标识性边框 */
}

.shooter-type-info {
  margin: 10px 0;
}

.info-row {
  display: flex;
  align-items: left;
  margin-bottom: 5px;
}

.info-label {
  margin-right: 10px;
}
</style>